import {
  Box,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Switch,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { SettingsCard } from "../card/SettingsCard";
import {
  GetWorkspaceClientPortalDto,
  GetWorkspaceDto,
  WorkspaceDto,
} from "@growth-machine-llc/stridist-api-client";
import { MealLoggingSettings } from "../meal-logging-settings/MealLoggingSettings";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import WorkspacesService from "../../services/WorkspacesService";
import {
  CURRENT_USER_QUERY_KEY,
  CURRENT_WORKSPACE_QUERY_KEY,
} from "../../wrappers/current-user/CurrentUserWrapper";
import { ActionButton } from "../button/ActionButton";
import { useSwitchUser } from "../../hooks/useSwitchUser";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import ViewAsSampleClientBanner from "../client-portal-settings/ViewAsSampleClientBanner";
import { COACH_SETTINGS_CLIENT_PORTAL_QUERY_KEY } from "../../routes/coach/settings/client-portal/CoachSettingsClientPortalRoute";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(3),

    "& > label": {
      flexGrow: 1,
    },
  },

  label: {
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },

  primaryText: {
    color: theme.palette.secondary.main,
    fontSize: 18,
    fontWeight: 600,
  },

  secondaryText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },

  optionsCategory: {
    width: "100%",
  },

  divider: {
    margin: theme.spacing(2, 0, 5, 0),
  },
}));

interface NutritionSettingsProps {
  workspace: GetWorkspaceClientPortalDto;
}

const queriesToInvalidateOnWorkspaceUpdate = [
  [CURRENT_WORKSPACE_QUERY_KEY],
  [CURRENT_USER_QUERY_KEY],
  [COACH_SETTINGS_CLIENT_PORTAL_QUERY_KEY],
];

export default function ClientPortalFeaturesSettings(
  props: NutritionSettingsProps,
) {
  const { workspace } = props;

  const s = useStyles();

  const queryClient = useQueryClient();

  const [nutritionsEnabled, setNutritionsEnabled] = React.useState(
    workspace.nutritionsEnabled,
  );

  const [assetSuggestionsEnabled, setAssetSuggestionsEnabled] = React.useState(
    workspace.assetSuggestionsEnabled,
  );

  const { mutate: updateNutrition } = useMutation({
    mutationFn: WorkspacesService.setNutritionEnabled,
    onSuccess: (_, vars) => {
      queriesToInvalidateOnWorkspaceUpdate.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey,
          refetchType: "all",
        });
      });
    },
  });

  const { mutate: updateMealLogging } = useMutation({
    mutationFn: WorkspacesService.setMealLogginsEnabled,
    onSuccess: (_, vars) => {
      queriesToInvalidateOnWorkspaceUpdate.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey,
          refetchType: "all",
        });
      });
    },
  });

  const { mutate: updateSuggestedAssets } = useMutation({
    mutationFn: WorkspacesService.setSuggestedAssetsEnabled,
    onSuccess: (_, vars) => {
      queriesToInvalidateOnWorkspaceUpdate.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey,
          refetchType: "all",
        });
      });
    },
  });

  const handleNutritionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: boolean,
  ) => {
    setNutritionsEnabled(value);
    updateNutrition({
      workspaceId: workspace.id,
      enabled: value,
    });
  };

  const handleMealLoggingChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: boolean,
  ) => {
    updateMealLogging({
      workspaceId: workspace.id,
      enabled: value,
    });
  };

  const handleAssetSuggestionsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: boolean,
  ) => {
    setAssetSuggestionsEnabled(value);
    updateSuggestedAssets({
      workspaceId: workspace.id,
      enabled: value,
    });
  };

  return (
    <Container>
      <SettingsCard
        header="Client portal settings"
        ButtonProps={{
          sx: { display: "none", visibility: "hidden" },
        }}
      >
        <FormControl component="fieldset" className={s.optionsCategory}>
          <FormLabel
            className={s.label}
            component="label"
            children="Food journal"
          />
          <MealLoggingSettings
            workspace={workspace}
            onChange={handleMealLoggingChange}
          />
        </FormControl>

        <Divider orientation="horizontal" className={s.divider} />

        <FormControl component="fieldset" className={s.optionsCategory}>
          <FormLabel
            className={s.label}
            component="label"
            children="Nutritions"
          />
          <FormControl component="div" className={s.formControl}>
            <FormLabel htmlFor="nutritions">
              <Typography className={s.primaryText}>
                Enable nutrition
              </Typography>
              <Typography className={s.secondaryText}>
                If enabled, clients can log nutrition in profile and check-ins
              </Typography>
            </FormLabel>

            <Switch
              id="nutrition"
              name="nutritions"
              checked={nutritionsEnabled}
              onChange={handleNutritionChange}
            />
          </FormControl>
        </FormControl>

        <Divider orientation="horizontal" className={s.divider} />

        <FormControl component="fieldset" className={s.optionsCategory}>
          <FormLabel
            className={s.label}
            component="label"
            children="Suggested exercise assets"
          />
          <FormControl component="div" className={s.formControl}>
            <FormLabel htmlFor="suggested-exercise-assets">
              <Typography className={s.primaryText}>
                Enable suggested exercise assets
              </Typography>
              <Typography className={s.secondaryText}>
                If enabled, clients can suggest new exercises to add to your
                library. These suggestions are immediately available to all your
                clients, even before you approve them.
              </Typography>
            </FormLabel>

            <Switch
              id="suggested-exercise-assets"
              name="suggested-exercise-assets"
              checked={assetSuggestionsEnabled}
              onChange={handleAssetSuggestionsChange}
            />
          </FormControl>
        </FormControl>

        <ViewAsSampleClientBanner />
      </SettingsCard>
    </Container>
  );
}
